import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { AlertCircle, Clock, CheckCircle, ArrowUpRight, Filter, Search, X } from "lucide-react";

const categoryConfig = {
  reclamation:   { label: "Réclamation",   color: "bg-red-100 text-red-700",      dot: "bg-red-500" },
  escalade:      { label: "Escalade",      color: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
  remboursement: { label: "Remboursement", color: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  rdv:           { label: "RDV",           color: "bg-blue-100 text-blue-700",     dot: "bg-blue-500" },
  reservation:   { label: "Réservation",   color: "bg-teal-100 text-teal-700",     dot: "bg-teal-500" },
  checkin:       { label: "Check-in",      color: "bg-cyan-100 text-cyan-700",     dot: "bg-cyan-500" },
  info:          { label: "Information",   color: "bg-gray-100 text-gray-600",     dot: "bg-gray-400" },
  urgence:       { label: "Urgence",       color: "bg-red-200 text-red-800",       dot: "bg-red-600" },
};

const priorityConfig = {
  critical: { label: "Critique", color: "text-red-600 bg-red-50 border-red-200" },
  high:     { label: "Haute",    color: "text-orange-600 bg-orange-50 border-orange-200" },
  normal:   { label: "Normale",  color: "text-gray-600 bg-gray-50 border-gray-200" },
  low:      { label: "Basse",    color: "text-blue-600 bg-blue-50 border-blue-200" },
};

const statusConfig = {
  open:      { label: "Ouvert",     color: "bg-orange-100 text-orange-700", icon: AlertCircle },
  pending:   { label: "En attente", color: "bg-amber-100 text-amber-700",   icon: Clock },
  resolved:  { label: "Résolu",     color: "bg-green-100 text-green-700",   icon: CheckCircle },
  escalated: { label: "Escaladé",   color: "bg-red-100 text-red-700",       icon: ArrowUpRight },
};

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Math.floor((new Date() - new Date(dateStr)) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `Il y a ${diff}min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
  return `Il y a ${Math.floor(diff / 1440)}j`;
}

function TicketDrawer({ ticket, onClose, onUpdate }) {
  const [status, setStatus] = useState(ticket.status || 'open');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const cat = categoryConfig[ticket.category] || categoryConfig.info;
  const pri = priorityConfig[ticket.priority] || priorityConfig.normal;

  async function save() {
    setSaving(true);
    try {
      const { data } = await supabase
        .from('tickets')
        .update({
          status,
          resolved_at: status === 'resolved' ? new Date() : null,
          updated_at: new Date()
        })
        .eq('id', ticket.id)
        .select()
        .single();
      onUpdate(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>

        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between z-10">
          <span className="text-sm font-medium text-gray-800">Détail du ticket</span>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-5">

          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.color}`}>
                {cat.label}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${pri.color}`}>
                {pri.label}
              </span>
            </div>
            <div className="text-sm font-medium text-gray-800">{ticket.subject || ticket.phone}</div>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span>📱 {ticket.phone}</span>
              <span>⏱ {timeAgo(ticket.created_at)}</span>
              <span className="capitalize">📡 {ticket.channel}</span>
            </div>
          </div>

          {ticket.subject && (
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="text-xs font-medium text-gray-500 mb-1">Message original</div>
              <p className="text-sm text-gray-700">{ticket.subject}</p>
            </div>
          )}

          <div>
            <div className="text-xs font-medium text-gray-700 mb-2">Statut</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(statusConfig).map(([key, val]) => {
                const Icon = val.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setStatus(key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
                      status === key
                        ? `${val.color} border-current`
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={13} />
                    {val.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-gray-700 mb-1">Notes internes</div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Ajouter une note sur ce ticket..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400 resize-none"
            />
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full py-2.5 bg-orange-600 text-white rounded-xl text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : saved ? '✓ Sauvegardé !' : 'Mettre à jour'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showFilter, setShowFilter] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }
      const { data: orgData } = await supabase
        .from('organizations')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (orgData?.id) {
        await loadTickets(orgData.id);
      }
      setLoading(false);
    }
    init();
  }, []);

  async function loadTickets(id) {
    try {
      const { data } = await supabase
        .from('tickets')
        .select('*')
        .eq('org_id', id)
        .order('created_at', { ascending: false });
      setTickets(data || []);
    } catch (e) {
      console.error(e);
    }
  }

  function handleUpdate(updated) {
    setTickets(prev => prev.map(t => t.id === updated.id ? updated : t));
    setSelected(updated);
  }

  const filtered = tickets.filter(t => {
    const matchSearch = !search ||
      (t.phone || '').includes(search) ||
      (t.subject || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchCat = filterCategory === 'all' || t.category === filterCategory;
    const matchPri = filterPriority === 'all' || t.priority === filterPriority;
    return matchSearch && matchStatus && matchCat && matchPri;
  });

  const stats = {
    total:    tickets.length,
    open:     tickets.filter(t => t.status === 'open').length,
    escalated:tickets.filter(t => t.status === 'escalated').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  };

  return (
    <div className="space-y-4">

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total",     value: stats.total,     color: "text-gray-800" },
          { label: "Ouverts",   value: stats.open,      color: "text-orange-600" },
          { label: "Escaladés", value: stats.escalated, color: "text-red-600" },
          { label: "Résolus",   value: stats.resolved,  color: "text-green-600" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className={`text-xl font-medium ${s.color}`}>
              {loading ? "..." : s.value}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-0 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un ticket..."
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400"
          />
        </div>
        <button
          onClick={() => setShowFilter(!showFilter)}
          className={`flex items-center gap-1.5 px-3 py-2 border rounded-xl text-sm transition-colors ${
            showFilter ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Filter size={14} />
          Filtrer
        </button>
      </div>

      {showFilter && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">Statut</div>
            <div className="flex gap-1.5 flex-wrap">
              {['all', 'open', 'pending', 'escalated', 'resolved'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    filterStatus === s ? 'bg-orange-100 text-orange-700 border-orange-300' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>
                  {s === 'all' ? 'Tous' : statusConfig[s]?.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">Catégorie</div>
            <div className="flex gap-1.5 flex-wrap">
              {['all', ...Object.keys(categoryConfig)].map(s => (
                <button key={s} onClick={() => setFilterCategory(s)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    filterCategory === s ? 'bg-orange-100 text-orange-700 border-orange-300' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>
                  {s === 'all' ? 'Toutes' : categoryConfig[s]?.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">Priorité</div>
            <div className="flex gap-1.5 flex-wrap">
              {['all', 'critical', 'high', 'normal', 'low'].map(s => (
                <button key={s} onClick={() => setFilterPriority(s)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    filterPriority === s ? 'bg-orange-100 text-orange-700 border-orange-300' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>
                  {s === 'all' ? 'Toutes' : priorityConfig[s]?.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">
            {filtered.length} ticket{filtered.length !== 1 ? 's' : ''}
          </span>
          {(filterStatus !== 'all' || filterCategory !== 'all' || filterPriority !== 'all') && (
            <button
              onClick={() => { setFilterStatus('all'); setFilterCategory('all'); setFilterPriority('all'); }}
              className="text-xs text-orange-600 hover:text-orange-700"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <div className="text-2xl mb-2">🎧</div>
            <div className="text-sm text-gray-500 font-medium">Aucun ticket pour l'instant</div>
            <div className="text-xs text-gray-400 mt-1">
              Les tickets apparaissent automatiquement quand Hellodia détecte une réclamation ou une escalade
            </div>
          </div>
        ) : (
          filtered.map((t, i) => {
            const cat = categoryConfig[t.category] || categoryConfig.info;
            const pri = priorityConfig[t.priority] || priorityConfig.normal;
            const sta = statusConfig[t.status] || statusConfig.open;
            const StatusIcon = sta.icon;
            return (
              <div key={i} onClick={() => setSelected(t)}
                className="px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${cat.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.color}`}>{cat.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${pri.color}`}>{pri.label}</span>
                      </div>
                      <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${sta.color}`}>
                        <StatusIcon size={10} />
                        {sta.label}
                      </div>
                    </div>
                    <div className="text-sm text-gray-800 mt-1 truncate">{t.subject || t.phone}</div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                      <span>📱 {t.phone}</span>
                      <span>⏱ {timeAgo(t.created_at)}</span>
                      <span className="capitalize">📡 {t.channel}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {selected && (
        <TicketDrawer
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}